import { Box } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { toast } from 'react-toastify';
import { OrganizationTitleConfigItems } from 'src/shared/components/organisms/organizationTitleConfigItems';
import { OrganizationTitleTypeEnum } from 'src/shared/models/enums/organizationTitleType.enum';
import { OrganizationTitleConfigRequest } from 'src/shared/models/organization/organizationTitleConfigRequest';
import { AxiosResponse } from 'axios';

type OrganizationTitleConfigModalProps<T> = {
  modalOpen: boolean;
  modalTitle: string;
  type: OrganizationTitleTypeEnum;
  organizationId: string | undefined;
  fetchMethod: () => Promise<AxiosResponse<any>>;
  updateMethod: (data: OrganizationTitleConfigRequest) => Promise<T>;
  onSave: (id?: number) => void;
  onClose: () => void;
};

export const OrganizationTitleConfigModal = <T,>(props: OrganizationTitleConfigModalProps<T>) => {
  const { modalOpen, modalTitle, type, organizationId, fetchMethod, updateMethod, onSave, onClose } = props;
  const { t } = useTranslation(['common', 'district']);
  const [loading, setLoading] = useState<boolean>(false);

  const isMounted = useIsMounted();

  const amountValidationMessage = t('common:validationMessages.amount');
  const requiredMessage = t('common:validationMessages.required');

  const organizationTitleConfigRowSchema = yup.object().shape({
    count: yup.number().min(0, amountValidationMessage).required(amountValidationMessage).typeError(amountValidationMessage),
    title: yup.string().required(requiredMessage),
    title_id: yup.number().required(requiredMessage),
  });

  const OrganizationTitleConfigRequestSchema: Schema<OrganizationTitleConfigRequest> = yup.object().shape({
    items: yup.array().of(organizationTitleConfigRowSchema).required(),
  });

  const formMethods = useForm<OrganizationTitleConfigRequest>({
    resolver: yupResolver(OrganizationTitleConfigRequestSchema),
    mode: 'all',
    defaultValues: {
      items: [{ count: 0, title: '', title_id: 0 }],
    },
  });

  const { handleSubmit, setValue, reset, trigger } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await fetchMethod();
        if (isMounted()) setValue('items', result.data);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchData();
  }, [fetchMethod, isMounted, organizationId, reset, setValue, type]);

  const saveFormData = useCallback(
    async (data: OrganizationTitleConfigRequest) => {
      setLoading(true);
      try {
        await updateMethod(data);
        if (isMounted()) {
          toast.success(<>{t('common:organization.titleConfigSaveSuccess')}</>);
          onSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onSave, t, updateMethod],
  );

  const onSubmit = useCallback(
    async (data: OrganizationTitleConfigRequest) => {
      ConsoleHelper.log('onSubmit', data);
      await trigger();
      await saveFormData(data);
    },
    [saveFormData, trigger],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={modalTitle}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 3 }}>
                <OrganizationTitleConfigItems />
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
    />
  );
};
